import React, { useState, useEffect, useMemo } from "react";
import Select from "../../Select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  Line,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content || typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1" />;

        if (trimmed.startsWith("### ")) {
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        }

        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">
                •
              </span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

// Y axis labels
const yAxisLabelGHG = "MT CO2e";
const yAxisDxGHG = -yAxisLabelGHG.length;

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg
      className="animate-spin h-5 w-5 text-[#59AD31]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      ></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

const ghgLegendOrder = [
  "crops",
  "livestock",
  "deforestation",
  "otherEmissions",
  "sequestration",
  "netAfolu",
  "target",
];

const ghgSourceRegionOptions = [
  "Net emissions",
  "Cropland",
  "Livestock",
  "Deforestation",
  "Other emissions",
  "Sequestration",
];

function getTooltipIcon(entry, chartType = "bar") {
  const key = entry.dataKey;

  if (key === "netAfolu") {
    return (
      <svg width="10" height="10" viewBox="0 0 10 10">
        <polygon
          points="5,1 9,5 5,9 1,5"
          fill="#FFFFFF"
          stroke="#0F0F0F"
          strokeWidth="1"
        />
      </svg>
    );
  }

  // Target metrics: red circle
  if (key && (key.includes("Target") || key.includes("target"))) {
    return (
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: "#FF0000" }}
      />
    );
  }

  // Line charts: SVG solid line
  if (chartType === "line") {
    return (
      <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
        <line x1="0" y1="1" x2="16" y2="1" stroke={entry.color} strokeWidth="2" />
      </svg>
    );
  }

  return (
    <span
      className="h-2 w-2 rounded-[2px]"
      style={{ backgroundColor: entry.color }}
    />
  );
}

function getDynamicColor(index) {
  const palette = [
    "#183B8C",
    "#A7A9AC",
    "#10B24A",
    "#72BCE3",
    "#F6B800",
    "#C65A11",
    "#FF5579",
    "#9999CC",
    "#009B8A",
    "#FB752E",
    "#6C5CE7",
    "#00B894",
    "#E17055",
    "#0984E3",
    "#D63031",
    "#636E72",
    "#00CEC9",
    "#E84393",
    "#2D3436",
    "#FDCB6E",
  ];

  return palette[index % palette.length];
}

// ───────── custom tooltip ─────────
function CustomTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">
        {label}
      </div>

      {sortedPayload.map((item) => (
        <div
          key={item.dataKey}
          className="mb-0.5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            {getTooltipIcon(item, chartType)}
            <span className="text-[#7E8081]">{item.name}</span>
          </div>

          <span className="font-medium text-[#323334]">
            {typeof item.value === "number"
              ? item.value.toLocaleString("en-US", {
                maximumFractionDigits: 0,
              })
              : item.value}{" "}
            <span className="text-[#98999A]">{yAxisLabelGHG}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function SourceRegionTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">
        {label}
      </div>

      {sortedPayload.map((item, idx) => (
        <div
          key={`${item.dataKey}-${idx}`}
          className="mb-0.5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            {getTooltipIcon(item, chartType)}
            <span className="text-[#7E8081]">{item.name}</span>
          </div>

          <span className="font-medium text-[#323334]">
            {typeof item.value === "number"
              ? item.value.toLocaleString("en-US", {
                maximumFractionDigits: 0,
              })
              : item.value}{" "}
            <span className="text-[#98999A]">{yAxisLabelGHG}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ───────── custom dot ─────────
const diamondDot = ({ cx, cy }) => {
  if (cx == null || cy == null) return null;
  const size = 6;
  const points = [
    `${cx},${cy - size}`,
    `${cx + size},${cy}`,
    `${cx},${cy + size}`,
    `${cx - size},${cy}`,
  ].join(" ");
  return (
    <polygon
      points={points}
      fill="#FFFFFF"
      stroke="#0F0F0F"
      strokeWidth={1}
    />
  );
};

// ───────── custom legend ─────────
function GHGLegend({ payload, hiddenKeys, onToggle, chartType = "bar", showTarget = true }) {
  if (!payload) return null;

  const byKey = Object.fromEntries(payload.map((p) => [p.dataKey, p]));
  const ordered = ghgLegendOrder.map((key) => byKey[key]).filter(Boolean);

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        const key = entry.dataKey;

        if (key === "target" && !showTarget) return null;

        const isNet = key === "netAfolu";
        const isTarget = key === "target";
        const isBar = [
          "crops",
          "livestock",
          "deforestation",
          "otherEmissions",
          "sequestration",
        ].includes(key);

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            {isBar && (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}

            {isNet && (
              <svg width="10" height="10" viewBox="0 0 10 10">
                <polygon
                  points="5,1 9,5 5,9 1,5"
                  fill="#FFFFFF"
                  stroke="#0F0F0F"
                  strokeWidth="1"
                />
              </svg>
            )}

            {isTarget && (
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: "#FF0000" }}
              />
            )}

            <span className="text-[12px]">
              {key === "netAfolu" ? "Net AFOLU" : key === "target" ? "Target 2050" : entry.value}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SourceRegionLegend({ payload, hiddenKeys, onToggle, chartType = "bar", showTarget = true, targetLabel = "Target 2050" }) {
  if (!payload) return null;

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {payload.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        const isTarget = entry.dataKey === "target";
        if (isTarget && !showTarget) return null;

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            {isTarget && (
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: "#FF0000" }}
              />
            )}
            {!isTarget && (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
            <span className="text-[12px]">{isTarget ? targetLabel : entry.value}</span>
          </button>
        );
      })}
    </div>
  );
}

function resolvePathwayNode(inputData, pathway) {
  if (!inputData || typeof inputData !== "object") return null;

  if (Array.isArray(inputData.charts)) return inputData;

  if (pathway && inputData[pathway] && typeof inputData[pathway] === "object") {
    return inputData[pathway];
  }

  const keys = Object.keys(inputData);
  if (keys.length === 1 && typeof inputData[keys[0]] === "object") {
    const candidate = inputData[keys[0]];
    if (Array.isArray(candidate?.charts)) return candidate;
  }

  return null;
}

function resolveGHGBlock(inputData, pathway) {
  if (!inputData || typeof inputData !== "object") return null;

  if (
    Array.isArray(inputData?.countries) ||
    inputData?.["GHG by sector"] ||
    inputData?.["GHG by source and region"]
  ) {
    return inputData;
  }

  if (inputData?.GHG) {
    return inputData.GHG;
  }

  const pathwayNode = resolvePathwayNode(inputData, pathway);
  if (pathwayNode?.charts?.[0]?.GHG) {
    return pathwayNode.charts[0].GHG;
  }

  return null;
}

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }

  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }

  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }

  return "Could not load analysis at this time.";
}

function getSelectValue(value) {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }
  return value ?? "";
}

export default function GHGTab({ data, pathway, pathwayId, scenathonId }) {
  const ghgBlock = useMemo(() => resolveGHGBlock(data, pathway), [data, pathway]);

  const resolvedPathwayNode = useMemo(
    () => resolvePathwayNode(data, pathway),
    [data, pathway],
  );

  const effectivePathwayId = pathwayId || resolvedPathwayNode?.id || null;

  const countries = useMemo(
    () => (Array.isArray(ghgBlock?.countries) ? ghgBlock.countries : []),
    [ghgBlock],
  );

  const defaultCountry = useMemo(() => {
    if (countries.includes("Global")) return "Global";
    return countries[0] || "";
  }, [countries]);

  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [selectedSourceRegionMetric, setSelectedSourceRegionMetric] =
    useState("Net emissions");
  const [hiddenGHGKeys, setHiddenGHGKeys] = useState([]);
  const [hiddenSourceRegionKeys, setHiddenSourceRegionKeys] = useState([]);
  const [hiddenCumulativeKeys, setHiddenCumulativeKeys] = useState([]);
  const [ghgAnalysis, setGhgAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [sourceRegionAnalysis, setSourceRegionAnalysis] = useState("");
  const [loadingSourceRegionAnalysis, setLoadingSourceRegionAnalysis] = useState(false);

  useEffect(() => {
    if (!selectedCountry && defaultCountry) {
      setSelectedCountry(defaultCountry);
    } else if (
      selectedCountry &&
      countries.length > 0 &&
      !countries.includes(selectedCountry)
    ) {
      setSelectedCountry(defaultCountry);
    }
  }, [selectedCountry, defaultCountry, countries]);

  const showGhgTarget = selectedCountry === "Global";

  const chartData = useMemo(() => {
    const block = ghgBlock?.["GHG by sector"];

    if (Array.isArray(block)) {
      return block.map((item) => ({
        ...item,
        target: showGhgTarget && item.year === 2050 ? 4000 : null,
      }));
    }
    if (!block || typeof block !== "object") return [];

    const series = block[selectedCountry];
    const data = Array.isArray(series) ? series : [];
    return data.map((item) => ({
      ...item,
      target: showGhgTarget && item.year === 2050 ? 4000 : null,
    }));
  }, [ghgBlock, selectedCountry, showGhgTarget]);

  const sourceRegionBlock = useMemo(() => {
    const block = ghgBlock?.["GHG by source and region"];
    return block && typeof block === "object" ? block : null;
  }, [ghgBlock]);

  const sourceRegionLabels = useMemo(() => {
    return Array.isArray(sourceRegionBlock?.labels)
      ? sourceRegionBlock.labels
      : [];
  }, [sourceRegionBlock]);

  const showSourceRegionTarget = selectedSourceRegionMetric === "Net emissions";

  const sourceRegionChartData = useMemo(() => {
    const metricBlock = sourceRegionBlock?.seriesByMetric;
    if (!metricBlock || typeof metricBlock !== "object") return [];

    // If "Net emissions" is selected, sum all other indicators
    if (selectedSourceRegionMetric === "Net emissions") {
      const indicatorsToSum = [
        "Cropland",
        "Livestock",
        "Deforestation",
        "Other emissions",
        "Sequestration",
      ];

      const seriesByIndicator = indicatorsToSum.map((ind) =>
        Array.isArray(metricBlock[ind]) ? metricBlock[ind] : []
      );

      // Find all years from the first series
      const firstSeries = seriesByIndicator[0];
      if (!Array.isArray(firstSeries) || firstSeries.length === 0) return [];

      // Sum values across all indicators for each year
      return firstSeries.map((yearItem, idx) => {
        const summedRegions = {};

        // Get all region keys from the first indicator's item
        const regions = Object.keys(yearItem).filter((key) => key !== "year");

        regions.forEach((region) => {
          let sum = 0;
          seriesByIndicator.forEach((series) => {
            const value = series[idx]?.[region];
            if (typeof value === "number") {
              sum += value;
            }
          });
          summedRegions[region] = sum;
        });

        return {
          year: yearItem.year,
          ...summedRegions,
          target: showSourceRegionTarget && yearItem.year === 2050 ? 4000 : null,
        };
      });
    }

    const series = metricBlock[selectedSourceRegionMetric];
    if (!Array.isArray(series)) return [];

    // Add target point for 2050 only when Net emissions is selected
    return series.map((item) => ({
      ...item,
      target: null,
    }));
  }, [sourceRegionBlock, selectedSourceRegionMetric, showSourceRegionTarget]);

  const cumulativeGHGChartData = useMemo(() => {
    const metricBlock = sourceRegionBlock?.seriesByMetric;
    const netSeries = Array.isArray(metricBlock?.["Net emissions"])
      ? metricBlock["Net emissions"]
      : [];

    if (netSeries.length === 0) return [];

    const regionTotals = {};
    let totalNetAfolu = 0;

    netSeries.forEach((item) => {
      const year = item?.year;
      if (typeof year !== "number" || year < 2025 || year > 2050) return;

      Object.keys(item).forEach((key) => {
        if (key === "year") return;
        const value = item[key];
        if (typeof value === "number") {
          regionTotals[key] = (regionTotals[key] || 0) + value;
          totalNetAfolu += value;
        }
      });
    });

    if (Object.keys(regionTotals).length === 0) return [];

    const rows = [
      {
        label: "2025–2050",
        ...regionTotals,
        target: 40000,
      },
    ];

    return rows;
  }, [sourceRegionBlock]);

  const cumulativeRegionLabels = useMemo(() => {
    if (!Array.isArray(cumulativeGHGChartData) || cumulativeGHGChartData.length === 0) {
      return [];
    }

    const firstRow = cumulativeGHGChartData[0];
    const keys = Object.keys(firstRow).filter((key) => key !== "label");

    if (Array.isArray(sourceRegionLabels) && sourceRegionLabels.length > 0) {
      return sourceRegionLabels.filter((label) => keys.includes(label));
    }

    return keys;
  }, [cumulativeGHGChartData, sourceRegionLabels]);

  const countryOptions = useMemo(
    () =>
      countries.map((countryName) => ({
        value: countryName,
        label: countryName,
      })),
    [countries],
  );

  const sourceRegionOptions = useMemo(
    () =>
      ghgSourceRegionOptions.map((item) => ({
        value: item,
        label: item,
      })),
    [],
  );

  useEffect(() => {
    setHiddenSourceRegionKeys([]);
  }, [selectedSourceRegionMetric]);

  useEffect(() => {
    if (
      !scenathonId ||
      !pathway ||
      !selectedCountry ||
      !Array.isArray(chartData) ||
      chartData.length === 0
    ) {
      setGhgAnalysis("");
      setLoadingAnalysis(false);
      return;
    }

    const controller = new AbortController();

    const fetchAnalysis = async () => {
      setLoadingAnalysis(true);
      try {
        const payload = {
          analysisKey: "ghg_analysis",
          scope: "country",
          scenathonId,
          pathway,
          country: selectedCountry,
          ghgData: {
            data: JSON.stringify(chartData),
            title: `GHG by sector - ${selectedCountry}`,
          },
          context: "ghg",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setGhgAnalysis(
          extractAnalysisText(resData, ["ghgAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching analysis:", error);
          setGhgAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingAnalysis(false);
        }
      }
    };

    fetchAnalysis();

    return () => controller.abort();
  }, [chartData, pathway, scenathonId, selectedCountry]);

  useEffect(() => {
    if (
      !scenathonId ||
      !pathway ||
      !selectedSourceRegionMetric ||
      !Array.isArray(sourceRegionChartData) ||
      sourceRegionChartData.length === 0
    ) {
      setSourceRegionAnalysis("");
      setLoadingSourceRegionAnalysis(false);
      return;
    }

    const controller = new AbortController();

    const fetchSourceRegionAnalysis = async () => {
      setLoadingSourceRegionAnalysis(true);
      try {
        const payload = {
          analysisKey: "ghg_source_region_analysis",
          scope: "metric",
          metric: selectedSourceRegionMetric,
          scenathonId,
          pathway,
          ghgSourceRegionData: {
            data: JSON.stringify(sourceRegionChartData),
            title: `GHG by source and region - ${selectedSourceRegionMetric}`,
          },
          context: "ghg",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setSourceRegionAnalysis(
          extractAnalysisText(resData, ["ghgSourceRegionAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching regional analysis:", error);
          setSourceRegionAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingSourceRegionAnalysis(false);
        }
      }
    };

    fetchSourceRegionAnalysis();

    return () => controller.abort();
  }, [sourceRegionChartData, pathway, scenathonId, selectedSourceRegionMetric]);

  const handleLegendClickGHG = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;

    if (dataKey === "target" && !showGhgTarget) return;

    setHiddenGHGKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickSourceRegion = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;

    setHiddenSourceRegionKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickCumulative = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;

    setHiddenCumulativeKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  return (
    <div className="flex w-full flex-col gap-10">
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            GHG by sector
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Country"
              value={selectedCountry}
              onChange={(value) => setSelectedCountry(getSelectValue(value))}
              options={countryOptions}
              placeholder="Select country"
              disabled={countryOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Array.isArray(chartData) ? chartData : []}
              stackOffset="sign"
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12, fontWeight: 700 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    maximumFractionDigits: 1,
                  })
                }
              >
                <Label
                  value={yAxisLabelGHG}
                  angle={-90}
                  position="left"
                  dx={yAxisDxGHG}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <CustomTooltip {...props} chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.03)" }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0, left: 0, width: "100%", justifyContent: "center" }}
                content={(props) => (
                  <GHGLegend
                    {...props}
                    hiddenKeys={hiddenGHGKeys}
                    onToggle={handleLegendClickGHG}
                    chartType="bar"
                    showTarget={showGhgTarget}
                  />
                )}
              />

              <Bar
                dataKey="crops"
                stackId="ghg"
                fill="#FFD786"
                name="Cropland"
                hide={hiddenGHGKeys.includes("crops")}
              />
              <Bar
                dataKey="livestock"
                stackId="ghg"
                fill="#FF5579"
                name="Livestock"
                hide={hiddenGHGKeys.includes("livestock")}
              />
              <Bar
                dataKey="deforestation"
                stackId="ghg"
                fill="#FB752E"
                name="Deforestation"
                hide={hiddenGHGKeys.includes("deforestation")}
              />
              <Bar
                dataKey="otherEmissions"
                stackId="ghg"
                fill="#80D5FF"
                name="Other emissions"
                hide={hiddenGHGKeys.includes("otherEmissions")}
              />
              <Bar
                dataKey="sequestration"
                stackId="ghg"
                fill="#CBF3DD"
                name="Sequestration + Regeneration"
                hide={hiddenGHGKeys.includes("sequestration")}
              />

              <Line
                type="monotone"
                dataKey="netAfolu"
                name="Net Afolu"
                stroke="transparent"
                dot={diamondDot}
                activeDot={diamondDot}
                hide={hiddenGHGKeys.includes("netAfolu")}
              />

              <Line
                type="monotone"
                dataKey="target"
                name="Target 2050"
                stroke="#FF0000"
                strokeWidth={3}
                dot={{ fill: "#FF0000", r: 6 }}
                isAnimationActive={false}
                hide={hiddenGHGKeys.includes("target")}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={ghgAnalysis} />
          )}
        </div> */}
      </section>

      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            GHG Emissions by source and country or region
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Indicators"
              value={selectedSourceRegionMetric}
              onChange={(value) => setSelectedSourceRegionMetric(getSelectValue(value))}
              options={sourceRegionOptions}
              placeholder="Select indicator"
              disabled={sourceRegionOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Array.isArray(sourceRegionChartData) ? sourceRegionChartData : []}
              stackOffset="sign"
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12, fontWeight: 700 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    maximumFractionDigits: 1,
                  })
                }
              >
                <Label
                  value={yAxisLabelGHG}
                  angle={-90}
                  position="left"
                  dx={yAxisDxGHG}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <SourceRegionTooltip {...props} chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.03)" }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0, left: 0, width: "100%", justifyContent: "center" }}
                content={(props) => (
                  <SourceRegionLegend
                    {...props}
                    hiddenKeys={hiddenSourceRegionKeys}
                    onToggle={handleLegendClickSourceRegion}
                    chartType="bar"
                    showTarget={showSourceRegionTarget}
                  />
                )}
              />

              {sourceRegionLabels.map((label, index) => {
                const color = getDynamicColor(index);

                return (
                  <Bar
                    key={label}
                    dataKey={label}
                    stackId="ghg-country"
                    name={label}
                    fill={color}
                    stroke={color}
                    hide={hiddenSourceRegionKeys.includes(label)}
                  />
                );
              })}

              {showSourceRegionTarget && (
                <Line
                  type="monotone"
                  dataKey="target"
                  name="Target 2050"
                  stroke="#FF0000"
                  strokeWidth={3}
                  dot={{ fill: "#FF0000", r: 6 }}
                  isAnimationActive={false}
                  hide={hiddenSourceRegionKeys.includes("target")}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingSourceRegionAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={sourceRegionAnalysis} />
          )}
        </div> */}
      </section>

      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Global cumulative AFOLU emissions: current distribution vs fair allocations across countries or regions
          </h2>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Array.isArray(cumulativeGHGChartData) ? cumulativeGHGChartData : []}
              stackOffset="sign"
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="label"
                tick={{ fill: "#98999A", fontSize: 12, fontWeight: 700 }}
              />

              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    maximumFractionDigits: 1,
                  })
                }
              >
                <Label
                  value={yAxisLabelGHG}
                  angle={-90}
                  position="left"
                  dx={yAxisDxGHG}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <SourceRegionTooltip {...props} chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.03)" }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0, left: 0, width: "100%", justifyContent: "center" }}
                content={(props) => (
                  <SourceRegionLegend
                    {...props}
                    hiddenKeys={hiddenCumulativeKeys}
                    onToggle={handleLegendClickCumulative}
                    chartType="bar"
                    showTarget={true}
                    targetLabel="Target"
                  />
                )}
              />

              {cumulativeRegionLabels.map((label, index) => {
                const color = getDynamicColor(index);
                return (
                  <Bar
                    key={label}
                    dataKey={label}
                    stackId="cumulative-regions"
                    name={label}
                    fill={color}
                    stroke={color}
                    hide={hiddenCumulativeKeys.includes(label)}
                  />
                );
              })}

              <Line
                type="monotone"
                dataKey="target"
                name="Target"
                stroke="#FF0000"
                strokeWidth={0}
                dot={{ fill: "#FF0000", r: 8 }}
                isAnimationActive={false}
                hide={hiddenCumulativeKeys.includes("target")}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}